import React, { useState } from "react";
import { searchCustomers, searchSales } from "../services/customerService";
import "./CustomerLookupTool.css";

const formatCustomerAddress = (customer) => {
  const street = [customer.address1, customer.address2].filter(Boolean).join(", ");
  const locality = [customer.city, customer.state, customer.zip_code].filter(Boolean).join(", ");
  return [street, locality, customer.country].filter(Boolean).join(", ") || "No address";
};

function CustomerLookupTool() {
  const [activeTab, setActiveTab] = useState("customers");

  // Customer search state
  const [customerParams, setCustomerParams] = useState({
    first_name: "", last_name: "", email: "",
    phone: "", account_id: "", source: "",
  });
  const [customerResults, setCustomerResults] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState(null);
  const [customerPage, setCustomerPage] = useState(1);

  // Sales search state
  const [salesParams, setSalesParams] = useState({
    customer_name: "", account_id: "", email: "",
  });
  const [salesResults, setSalesResults] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState(null);
  const [salesPage, setSalesPage] = useState(1);

  const handleCustomerSearch = async (page = 1) => {
    setCustomerLoading(true);
    setCustomerError(null);
    try {
      const data = await searchCustomers({ ...customerParams, page });
      setCustomerResults(data);
      setCustomerPage(page);
    } catch (err) {
      setCustomerError("Search failed. Please try again.");
    } finally {
      setCustomerLoading(false);
    }
  };

  const handleSalesSearch = async (page = 1) => {
    setSalesLoading(true);
    setSalesError(null);
    try {
      const data = await searchSales({ ...salesParams, page });
      setSalesResults(data);
      setSalesPage(page);
    } catch (err) {
      setSalesError("Search failed. Please try again.");
    } finally {
      setSalesLoading(false);
    }
  };

  return (
    <div className="lookup-wrapper">
      <h2 className="lookup-title">Customer Lookup</h2>

      {/* Tabs */}
      <div className="lookup-tabs">
        <button
          className={`lookup-tab ${activeTab === "customers" ? "active" : ""}`}
          onClick={() => setActiveTab("customers")}
        >
          Customers
        </button>
        <button
          className={`lookup-tab ${activeTab === "sales" ? "active" : ""}`}
          onClick={() => setActiveTab("sales")}
        >
          Sales
        </button>
      </div>

      {/* Customer Search */}
      {activeTab === "customers" && (
        <div className="lookup-panel">
          <div className="lookup-search-card">
            <div className="lookup-fields-grid">
              <div className="lookup-field">
                <label>First Name</label>
                <input
                  type="text"
                  placeholder="e.g. Anna"
                  value={customerParams.first_name}
                  onChange={(e) =>
                    setCustomerParams({ ...customerParams, first_name: e.target.value })
                  }
                />
              </div>
              <div className="lookup-field">
                <label>Last Name</label>
                <input
                  type="text"
                  placeholder="e.g. Smith"
                  value={customerParams.last_name}
                  onChange={(e) =>
                    setCustomerParams({ ...customerParams, last_name: e.target.value })
                  }
                />
              </div>
              <div className="lookup-field">
                <label>Email</label>
                <input
                  type="text"
                  placeholder="e.g. anna@email.com"
                  value={customerParams.email}
                  onChange={(e) =>
                    setCustomerParams({ ...customerParams, email: e.target.value })
                  }
                />
              </div>
              <div className="lookup-field">
                <label>Phone</label>
                <input
                  type="text"
                  placeholder="e.g. 9202682756"
                  value={customerParams.phone}
                  onChange={(e) =>
                    setCustomerParams({ ...customerParams, phone: e.target.value })
                  }
                />
              </div>
              <div className="lookup-field">
                <label>Account ID</label>
                <input
                  type="text"
                  placeholder="e.g. 100509"
                  value={customerParams.account_id}
                  onChange={(e) =>
                    setCustomerParams({ ...customerParams, account_id: e.target.value })
                  }
                />
              </div>
              <div className="lookup-field">
                <label>Source</label>
                <select
                  value={customerParams.source}
                  onChange={(e) =>
                    setCustomerParams({ ...customerParams, source: e.target.value })
                  }
                >
                  <option value="">All Sources</option>
                  <option value="lightspeed">Lightspeed</option>
                  <option value="rics">RICS</option>
                </select>
              </div>
            </div>
            <button
              className="lookup-search-btn"
              onClick={() => handleCustomerSearch(1)}
              disabled={customerLoading}
            >
              {customerLoading ? "Searching..." : "Search"}
            </button>
          </div>

          {customerError && <p className="lookup-error">{customerError}</p>}

          {customerResults && (
            <div className="lookup-results">
              <div className="lookup-results-header">
                <span>{customerResults.total} results found</span>
                <span>Page {customerResults.page} of {customerResults.total_pages}</span>
              </div>

              {customerResults.customers.length === 0 ? (
                <p className="lookup-no-results">No customers found.</p>
              ) : (
                customerResults.customers.map((c) => (
                  <div key={c.id} className="lookup-result-card">
                    <div className="lookup-result-main">
                      <div>
                        <p className="lookup-result-name">
                          <span className="lookup-result-label">Customer:</span>{" "}
                          {c.full_name ||
                            `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
                            "—"}
                        </p>
                        <p className="lookup-result-detail">
                          <span className="lookup-result-label">Email:</span>{" "}
                          {c.email || "No email"} ·{" "}
                          <span className="lookup-result-label">Phone number -</span>{" "}
                          {c.phone_home || c.phone_mobile || c.phone_work || "No phone"}
                        </p>
                        <p className="lookup-result-detail">
                          <span className="lookup-result-label">Address:</span>{" "}
                          {formatCustomerAddress(c)}
                        </p>
                      </div>
                      <div className="lookup-result-right">
                        <span className={`lookup-source-badge ${c.source}`}>
                          Source: {c.source || "—"}
                        </span>
                        <p className="lookup-result-id">
                          <span className="lookup-result-label">Account ID:</span>{" "}
                          {c.account_id || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}

              <div className="lookup-pagination">
                <button
                  disabled={!customerResults.has_prev}
                  onClick={() => handleCustomerSearch(customerPage - 1)}
                >
                  ← Prev
                </button>
                <span>
                  Page {customerResults.page} of {customerResults.total_pages}
                </span>
                <button
                  disabled={!customerResults.has_next}
                  onClick={() => handleCustomerSearch(customerPage + 1)}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sales Search */}
      {activeTab === "sales" && (
        <div className="lookup-panel">
          <div className="lookup-search-card">
            <div className="lookup-fields-grid">
              <div className="lookup-field">
                <label>Customer Name</label>
                <input
                  type="text"
                  placeholder="e.g. Kenneth Zaremski"
                  value={salesParams.customer_name}
                  onChange={(e) =>
                    setSalesParams({ ...salesParams, customer_name: e.target.value })
                  }
                />
              </div>
              <div className="lookup-field">
                <label>Account ID</label>
                <input
                  type="text"
                  placeholder="e.g. 100509"
                  value={salesParams.account_id}
                  onChange={(e) =>
                    setSalesParams({ ...salesParams, account_id: e.target.value })
                  }
                />
              </div>
              <div className="lookup-field">
                <label>Email</label>
                <input
                  type="text"
                  placeholder="e.g. anna@email.com"
                  value={salesParams.email}
                  onChange={(e) =>
                    setSalesParams({ ...salesParams, email: e.target.value })
                  }
                />
              </div>
            </div>
            <button
              className="lookup-search-btn"
              onClick={() => handleSalesSearch(1)}
              disabled={salesLoading}
            >
              {salesLoading ? "Searching..." : "Search"}
            </button>
          </div>

          {salesError && <p className="lookup-error">{salesError}</p>}

          {salesResults && (
            <div className="lookup-results">
              <div className="lookup-results-header">
                <span>{salesResults.total} results found</span>
                <span>Page {salesResults.page} of {salesResults.total_pages}</span>
              </div>

              {salesResults.sales.length === 0 ? (
                <p className="lookup-no-results">No sales found.</p>
              ) : (
                salesResults.sales.map((s) => (
                  <div key={s.id} className="lookup-result-card">
                    <div className="lookup-result-main">
                      <div>
                        <p className="lookup-result-name">
                          <span className="lookup-result-label">Customer:</span>{" "}
                          {s.customer_name || "—"}
                        </p>
                        <p className="lookup-result-detail">
                          <span className="lookup-result-label">Email:</span>{" "}
                          {s.email || s.customer_email || "No email"}
                        </p>
                        <p className="lookup-result-detail">
                          <span className="lookup-result-label">SKU:</span> {s.sku || "—"} ·{" "}
                          <span className="lookup-result-label">Product:</span>{" "}
                          {s.description || "—"}
                        </p>
                        <p className="lookup-result-detail">
                          <span className="lookup-result-label">Ticket:</span>{" "}
                          {s.ticket || "—"} ·{" "}
                          <span className="lookup-result-label">Salesperson:</span>{" "}
                          {s.salesperson || "—"}
                        </p>
                        <p className="lookup-result-detail">
                          <span className="lookup-result-label">Sale date:</span>{" "}
                          {s.date || "—"}
                        </p>
                      </div>
                      <div className="lookup-result-right">
                        <p className="lookup-result-price">
                          <span className="lookup-result-label">Net sales:</span>{" "}
                          ${s.net_sales?.toFixed(2) || "0.00"}
                        </p>
                        <p className="lookup-result-id">
                          <span className="lookup-result-label">Account ID:</span>{" "}
                          {s.account_id || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}

              <div className="lookup-pagination">
                <button
                  disabled={!salesResults.has_prev}
                  onClick={() => handleSalesSearch(salesPage - 1)}
                >
                  ← Prev
                </button>
                <span>
                  Page {salesResults.page} of {salesResults.total_pages}
                </span>
                <button
                  disabled={!salesResults.has_next}
                  onClick={() => handleSalesSearch(salesPage + 1)}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export default CustomerLookupTool;
